import { useContext } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';
import { clampToInteger, toBound } from './clamp-to-integer';

/**
 * IntegerField — champ numérique dont le STORE ne porte jamais autre chose
 * qu'un entier borné (ou '' quand le champ est vidé).
 *
 * Pourquoi du JS et pas du YAML : `min`/`max`/`step` d'un input HTML ne sont
 * que des indices tant qu'aucun formulaire n'est soumis — or les fiches
 * enregistrent AU REPOS (DataSync), sans soumission. Une valeur décimale ou
 * hors bornes tapée au clavier partirait donc telle quelle au backend. Le
 * transformateur d'écriture de l'`Input` de reactive-json (`valueToStore`)
 * ferait l'affaire mais n'est pas exposé en YAML : c'est ce chaînon que ce
 * champ ajoute, sans rien connaître du métier qui l'utilise.
 *
 * Props YAML :
 *   dataLocation     – liaison de formulaire standard
 *   min / max        – bornes INCLUSES (facultatives)
 *   placeholder
 *   attributes       – sur l'enveloppe
 *   inputAttributes  – sur l'<input> (class, id, aria-*…)
 *
 * Exemple :
 *   - type: IntegerField
 *     dataLocation: ~~.deal.data.forecastProbability
 *     min: 0
 *     max: 100
 *     inputAttributes: { class: "input", id: "d-proba" }
 */
export const IntegerField = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const resolve = (valueToEvaluate) =>
        evaluateTemplateValue({ valueToEvaluate, globalDataContext, templateContext });

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: '',
        globalDataContext,
        templateContext,
    });

    const attributes = useEvaluatedAttributes(props.attributes ?? {});
    const inputAttributes = useEvaluatedAttributes(props.inputAttributes ?? {});
    const min = toBound(resolve(props.min));
    const max = toBound(resolve(props.max));

    const handleChange = (event) => {
        const input = event.currentTarget;
        // Un input[type=number] rend '' pour toute saisie non parsable ('40.',
        // '-', '1e'). Impossible de distinguer ce cas d'un champ vidé par la
        // seule valeur : validity.badInput le fait. On garde alors la valeur
        // précédente, sinon le champ se viderait dès la frappe d'un séparateur.
        if (input.value === '' && input.validity?.badInput) return;
        globalDataContext.updateData(clampToInteger(input.value, min, max), formDataPath);
    };

    // Bornes reposées sur l'input : elles ne garantissent rien (cf. plus haut)
    // mais donnent le bon clavier mobile et le bon rôle aux technologies
    // d'assistance. Le pas est entier par construction.
    const input = (
        <input
            {...inputAttributes}
            type="number"
            inputMode="numeric"
            step="1"
            min={min ?? undefined}
            max={max ?? undefined}
            placeholder={resolve(props.placeholder) ?? ''}
            value={formData ?? ''}
            onChange={handleChange}
        />
    );

    return (
        <ActionDependant {...props}>
            {Object.keys(attributes).length > 0 ? <div {...attributes}>{input}</div> : input}
        </ActionDependant>
    );
};
